import React from "react";
import ThirdPartyVideoDialogHeader from "./ThirdPartyVideoDialogHeader";
import { ThirdPartyConfigDto } from "@repo/api/third-party-config";
import { IconAlertCircle } from "@tabler/icons-react";
import { Button } from "@/lib/components/Button/Button";
import { useNavigate, useParams } from "react-router";
import { paths } from "@/App";
import {
  TP_VIDEO_INTERNAL_NAME_STORAGE_KEY,
  TP_VIDEO_TOKEN_STORAGE_KEY,
} from "@/lib/session-storage";
import { TeamSwitcher } from "@/lib/components/TeamSwitcher";
import {
  thirdPartyQueryKeys,
  useUpsertThirdPartyVideoMutation,
} from "@/lib/queries/third-party-queries";
import { queryClient } from "@/lib/queries/query-client";
import LoadingSpinner from "@/lib/components/LoadingSpinner";

function ThirdPartyVideoDialogBase({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center">
      <div className="flex h-135 max-h-[90vh] w-195 max-w-full flex-col overflow-y-auto rounded-xl border bg-background px-4 shadow-lg md:px-10">
        {children}
      </div>
    </div>
  );
}

type PublicProps = {
  thirdPartyConfig: ThirdPartyConfigDto;
  token: string;
};

export function ThirdPartyVideoDialogPublic({
  thirdPartyConfig,
  token,
}: PublicProps) {
  const navigate = useNavigate();

  const navigateAndStoreToken = (path: string) => {
    sessionStorage.setItem(TP_VIDEO_TOKEN_STORAGE_KEY, token);
    sessionStorage.setItem(
      TP_VIDEO_INTERNAL_NAME_STORAGE_KEY,
      thirdPartyConfig.internalName,
    );
    navigate(path);
  };

  return (
    <ThirdPartyVideoDialogBase>
      <ThirdPartyVideoDialogHeader thirdPartyConfig={thirdPartyConfig} />
      <div className="flex flex-1 flex-col items-center justify-end gap-3 py-10">
        <Button
          variant="outline"
          size="sm"
          className="min-w-54"
          onClick={() => {
            navigateAndStoreToken(paths.signIn());
          }}
        >
          Sign in
        </Button>
        <Button
          className="min-w-54"
          size="sm"
          onClick={() => {
            navigateAndStoreToken(paths.signUp());
          }}
        >
          Get started for free
        </Button>
        <p className="text-center text-sm text-text-secondary">
          Free video with 8 images for new users
        </p>
      </div>
    </ThirdPartyVideoDialogBase>
  );
}

type TeamProps = {
  thirdPartyConfig: ThirdPartyConfigDto;
  token: string;
};

export function ThirdPartyVideoDialogTeam({
  thirdPartyConfig,
  token,
}: TeamProps) {
  const navigate = useNavigate();
  const { teamExternalId } = useParams();

  const {
    mutate: upsertThirdPartyVideo,
    isPending: isUpsertThirdPartyVideoPending,
    isError: isUpsertThirdPartyVideoError,
  } = useUpsertThirdPartyVideoMutation({
    teamExternalId: teamExternalId!,
    token,
    onSuccess: async (data) => {
      queryClient.invalidateQueries({
        queryKey: thirdPartyQueryKeys.all(),
      });

      navigate(paths.videoEdit(teamExternalId!, data.videoExternalId));
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const isExistingVideo = thirdPartyConfig.existingVideos.some(
    (video) => video.teamExternalId === teamExternalId,
  );

  if (isUpsertThirdPartyVideoError) {
    return (
      <ThirdPartyVideoDialogError
        title="Failed to load images"
        description="Please try again or contact support"
      >
        <Button
          size="sm"
          variant="outline"
          className="mt-4"
          disabled={isUpsertThirdPartyVideoPending}
          onClick={() => {
            upsertThirdPartyVideo();
          }}
        >
          Try again
        </Button>
      </ThirdPartyVideoDialogError>
    );
  }

  if (isUpsertThirdPartyVideoPending) {
    return (
      <ThirdPartyVideoDialogBase>
        <ThirdPartyVideoDialogLoading thirdPartyConfig={thirdPartyConfig} />
      </ThirdPartyVideoDialogBase>
    );
  }

  return (
    <ThirdPartyVideoDialogBase>
      <ThirdPartyVideoDialogHeader
        thirdPartyConfig={thirdPartyConfig}
        isExistingVideo={isExistingVideo}
      />
      <div className="flex-1"></div>
      <div className="flex items-center justify-between gap-3 py-10">
        <TeamSwitcher
          classNameTrigger="px-3 py-2 rounded-md min-w-54 hover:bg-accent"
          isUserVisible
          teamExternalId={teamExternalId!}
          onTeamSwitch={(newTeamExternalId) => {
            navigate(paths.thirdPartyVideoTeam(newTeamExternalId, token));
          }}
          signOutRedirectUrl={paths.thirdPartyVideoPublic(token)}
        />
        <Button
          size="sm"
          disabled={isUpsertThirdPartyVideoPending}
          onClick={() => {
            upsertThirdPartyVideo();
          }}
        >
          {isExistingVideo ? "Edit video" : "Create video"}
        </Button>
      </div>
    </ThirdPartyVideoDialogBase>
  );
}

type ErrorProps = {
  title: string;
  description: string;
  children?: React.ReactNode;
};

export function ThirdPartyVideoDialogError({
  title,
  description,
  children,
}: ErrorProps) {
  return (
    <ThirdPartyVideoDialogBase>
      <div className="flex h-full w-full flex-col items-center justify-center">
        <IconAlertCircle className="h-10 w-10 text-error" stroke={1.5} />
        <h1 className="mt-3 text-center text-xl font-semibold">{title}</h1>
        <p className="mt-1 text-center text-base text-text-secondary">
          {description}
        </p>
        {children}
      </div>
    </ThirdPartyVideoDialogBase>
  );
}

export function ThirdPartyVideoDialogLoading({
  thirdPartyConfig,
}: {
  thirdPartyConfig: ThirdPartyConfigDto;
}) {
  return (
    <ThirdPartyVideoDialogBase>
      <div className="flex h-full w-full flex-col items-center justify-center">
        <LoadingSpinner size={32} />
        <h1 className="mt-3 text-center text-xl font-semibold">{`Loading images from ${thirdPartyConfig.displayName}`}</h1>
        <p className="mt-1 text-center text-base text-text-secondary">
          You’ll be soon creating stunning videos
        </p>
      </div>
    </ThirdPartyVideoDialogBase>
  );
}
